// hooks/useTextManagement.js
import { useState, useEffect, useMemo } from 'react';
import { textFilterByStatus } from 'helpers/FilterOrders';
import { lower } from 'utils/StringUtils';
import { useTextFilter } from 'utils/hooks/useTextFilter';

export const useTextManagement = (
  role,
  textList,
  selectedStatus,
  selectedType,
  searchValue,
  textsPerPage,
  currentPage
) => {
  const { isDelivered, isRevisionSent, isRevisionDone } =
    useTextFilter(textList);
  const [filteredTexts, setFilteredTexts] = useState([]);
  const [sortedTexts, setSortedTexts] = useState([]);
  const [currentTexts, setCurrentTexts] = useState([]);
  const [pageNumbers, setPageNumbers] = useState([]);

  // Filtering by type
  useEffect(() => {
    const filterByType = (text) => {
      if (!selectedType?.field?.length) return true;
      if (selectedType.label === 'COMBINED') return text.type === 'COMBINED';
      if (selectedType.label === 'TRANSLATED')
        return text.type === 'TRANSLATED';
      if (selectedType.label === 'NON_TRANSLATED')
        return text.type === 'NON_TRANSLATED';
      return true;
    };
    setFilteredTexts(textList.filter(filterByType));
  }, [selectedType, textList]);

  // Filtering by search
  useEffect(() => {
    if (searchValue) {
      const filtered = textList.filter((text) =>
        lower(text.keyword).includes(lower(searchValue))
      );
      setFilteredTexts(filtered);
    } else {
      setFilteredTexts([...textList]);
    }
  }, [searchValue, textList]);

  // Filtering by status
  useEffect(() => {
    const filtered = textFilterByStatus(textList, selectedStatus);
    setFilteredTexts(filtered);
  }, [selectedStatus, textList]);

  // Sort by validated status
  useEffect(() => {
    const clientSortedTexts = [
      ...filteredTexts.filter((text) => isDelivered(text)),
      ...filteredTexts.filter((text) => isRevisionDone(text)),
      ...filteredTexts.filter((text) => isRevisionSent(text)),
      ...filteredTexts.filter((text) => text.validated),
    ];
    const sorted = [
      ...filteredTexts.filter((text) => !text.validated),
      ...filteredTexts.filter((text) => text.validated),
    ];
    setSortedTexts(role === 'CLIENT' ? clientSortedTexts : sorted);
  }, [filteredTexts]);

  // Pagination
  useEffect(() => {
    const lastIdx = currentPage * textsPerPage;
    const firstIdx = lastIdx - textsPerPage;
    setCurrentTexts(sortedTexts.slice(firstIdx, lastIdx));
    setPageNumbers(
      [...Array(Math.ceil(sortedTexts.length / textsPerPage)).keys()].map(
        (i) => i + 1
      )
    );
  }, [sortedTexts, currentPage, textsPerPage]);

  return {
    filteredTexts,
    sortedTexts,
    currentTexts,
    pageNumbers,
  };
};
